import React, { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Eye, Plus } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import SearchSelect from "../../components/SearchSelect";
import { useAuth } from "../../context/AuthContext";
import {
  chat as api,
  residents as residentsApi,
  type PrivateMessage,
  type ConversationSummary,
  type ChatParticipantType,
  type Resident,
  type ApiError,
} from "../../services/api";

const inp = {
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
};

interface Party {
  type: ChatParticipantType;
  id: string;
  name: string;
}

const AdminMessagesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const myId = user?.id;

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState("");

  const [partyA, setPartyA] = useState<Party | null>(null);
  const [partyB, setPartyB] = useState<Party | null>(null);
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [showNewMessage, setShowNewMessage] = useState(false);
  const [residentOptions, setResidentOptions] = useState<Resident[]>([]);
  const [newMessageResidentId, setNewMessageResidentId] = useState("");

  const loadConversations = () => {
    if (!societyId) return;
    setConvLoading(true);
    setConvError("");
    api
      .adminConversations(societyId)
      .then(setConversations)
      .catch((err) => setConvError((err as ApiError).message || "Failed to load conversations."))
      .finally(() => setConvLoading(false));
  };

  useEffect(loadConversations, [societyId]);

  useEffect(() => {
    if (!showNewMessage || !societyId || residentOptions.length > 0) return;
    residentsApi.list(societyId, { size: 500 }).then((p) => setResidentOptions(p.content)).catch(() => {});
  }, [showNewMessage, societyId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const iAmParticipant = (a: Party, b: Party) =>
    (a.type === "admin" && a.id === myId) || (b.type === "admin" && b.id === myId);

  const openConversation = async (a: Party, b: Party) => {
    if (!societyId) return;
    setPartyA(a);
    setPartyB(b);
    setShowNewMessage(false);
    setThreadLoading(true);
    setError("");
    try {
      const msgs = iAmParticipant(a, b)
        ? await api.thread(societyId, a.type === "admin" && a.id === myId ? b.type : a.type, a.type === "admin" && a.id === myId ? b.id : a.id)
        : await api.adminThread(societyId, a.type, a.id, b.type, b.id);
      setMessages(msgs);
    } catch (err) {
      setError((err as ApiError).message || "Failed to load conversation.");
    } finally {
      setThreadLoading(false);
    }
  };

  const openFromSummary = (c: ConversationSummary) => {
    openConversation(
      { type: c.aType, id: c.aId, name: c.aName },
      { type: c.bType, id: c.bId, name: c.bName },
    );
  };

  const startNewMessage = () => {
    const resident = residentOptions.find((r) => r.id === newMessageResidentId);
    if (!resident || !myId) return;
    openConversation(
      { type: "admin", id: myId, name: "You" },
      { type: "resident", id: resident.id, name: resident.fullName },
    );
    setNewMessageResidentId("");
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !partyA || !partyB || !body.trim() || !myId) return;
    const other = partyA.type === "admin" && partyA.id === myId ? partyB : partyA;
    setSending(true);
    try {
      const sent = await api.send(societyId, other.type, other.id, body.trim());
      setMessages((p) => [...p, sent]);
      setBody("");
      loadConversations();
    } catch (err) {
      setError((err as ApiError).message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const viewingAsParticipant = partyA && partyB && iAmParticipant(partyA, partyB);

  return (
    <PageLayout>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Messages
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            Message a resident directly, or review any conversation in this society
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewMessage((v) => !v)}
          className="nx-btn nx-btn-primary"
          style={{ gap: 6 }}
        >
          <Plus size={14} /> New Message
        </button>
      </div>

      {showNewMessage && (
        <div className="nx-card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 8 }}>
            Message a Resident
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <SearchSelect
                value={newMessageResidentId}
                onChange={setNewMessageResidentId}
                options={residentOptions.map((r) => ({
                  value: r.id,
                  label: r.unitNumber ? `${r.fullName} — ${r.unitNumber}` : r.fullName,
                }))}
                placeholder="Search residents…"
              />
            </div>
            <button
              type="button"
              onClick={startNewMessage}
              disabled={!newMessageResidentId}
              className="nx-btn nx-btn-secondary"
            >
              Start
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16 }}>
        <div className="nx-card" style={{ padding: 0, overflow: "hidden", height: 480, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--nx-border)", fontSize: 12, fontWeight: 600, color: "var(--nx-text-secondary)" }}>
            Conversations
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {convLoading && <div style={{ padding: 16, fontSize: 13, color: "var(--nx-text-muted)" }}>Loading…</div>}
            {convError && <div style={{ padding: 16, fontSize: 13, color: "#EF4444" }}>{convError}</div>}
            {!convLoading && !convError && conversations.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: "var(--nx-text-muted)" }}>No conversations yet.</div>
            )}
            {conversations.map((c, i) => {
              const active = partyA?.id === c.aId && partyB?.id === c.bId;
              const iAmIn = iAmParticipant(
                { type: c.aType, id: c.aId, name: c.aName },
                { type: c.bType, id: c.bId, name: c.bName },
              );
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => openFromSummary(c)}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "10px 16px",
                    background: active ? "var(--nx-surface-2)" : "transparent",
                    border: "none", borderBottom: "1px solid var(--nx-border)", cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, color: "var(--nx-text-primary)" }}>
                    {!iAmIn && <Eye size={11} style={{ color: "var(--nx-text-muted)" }} />}
                    {c.aName} ↔ {c.bName}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--nx-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.lastMessage}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="nx-card" style={{ padding: 16, display: "flex", flexDirection: "column", height: 480 }}>
          {!partyA || !partyB ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--nx-text-muted)" }}>
              <MessageCircle size={28} />
              <p style={{ fontSize: 13, marginTop: 8 }}>Select a conversation, or start a new message.</p>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 8 }}>
                {partyA.name} ↔ {partyB.name}
                {!viewingAsParticipant && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: "var(--nx-text-muted)" }}>
                    (viewing for moderation — you're not part of this conversation)
                  </span>
                )}
              </div>
              {error && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 8 }}>{error}</div>}
              <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {threadLoading && <div style={{ color: "var(--nx-text-muted)", fontSize: 13 }}>Loading…</div>}
                {!threadLoading && messages.length === 0 && (
                  <div style={{ color: "var(--nx-text-muted)", fontSize: 13 }}>No messages yet.</div>
                )}
                {messages.map((m) => {
                  const mine = m.senderType === "admin" && m.senderId === myId;
                  const senderName = m.senderType === partyA.type && m.senderId === partyA.id ? partyA.name : partyB.name;
                  return (
                    <div key={m.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                      {!viewingAsParticipant && (
                        <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginBottom: 2 }}>{senderName}</div>
                      )}
                      <div style={{
                        background: mine ? "var(--nx-accent)" : "var(--nx-surface-3)",
                        color: mine ? "#fff" : "var(--nx-text-primary)",
                        padding: "8px 12px", borderRadius: 12, fontSize: 13,
                      }}>
                        {m.body}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 2, textAlign: mine ? "right" : "left" }}>
                        {new Date(m.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              {viewingAsParticipant ? (
                <form onSubmit={handleSend} style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" style={{ ...inp, flex: 1 }} />
                  <button type="submit" disabled={sending || !body.trim()} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
                    <Send size={14} /> Send
                  </button>
                </form>
              ) : (
                <div style={{ marginTop: 12, fontSize: 11, color: "var(--nx-text-muted)" }}>
                  Read-only — you can't send messages in a conversation you're not part of.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminMessagesPage;
